// ===================================================================
// components/ProfilePage.tsx
// Page Profil — 5 sections : Identité, Pseudo, Paramètres, Stats, Communauté
// ===================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Unlock, Check, Globe, Award, Swords, AlertTriangle, X, Flame, Bell, LogOut, Users } from 'lucide-react';
import { useTheme } from './ThemeContext';
import InterfaceLanguageSelector from './InterfaceLanguageSelector';
import ProgressionPage from './ProgressionPage';
import { LanguageCode, UserProgress } from '../types/types';
import { SubscriptionTier } from '../services/subscriptionService';
import {
  upsertProfile,
  searchUsers,
  followUser,
  unfollowUser,
  getFollowing,
  getLeaderboard,
  isFollowing as checkIsFollowing,
  getMyProfile,
  getLanguageInfo,
  UserProfile,
  displayXP,
  getWeeklyXP,
  timeUntilWeekReset,
} from '../services/friendsService';
import secureAuthService from '../services/secureAuthService';
import { requestAndSubscribe, getNotificationStatus, areRemindersEnabled, disableReminders, NotifStatus, getLocalNotifPrefs, loadNotifPrefs, saveNotifPref, NotifPrefKey, NotifPrefs } from '../services/notificationService';
import './ProfilePage.css';
import TribalBorder from './TribalBorder';
import { getLevel } from '../data/levelTitles';
import Footer from './Footer';
import { getCollectibles, getUnlockedIds } from '../utils/collectibles';
import { UNIT_CONFIGS } from './CategorySelection';
import CollectibleIcon, { DiamondIcon } from './CollectibleIcon';

import { EmojiText, Picto } from './common/Picto';
// ---------------------------------------------------------------
// Helpers XP / Niveau
// ---------------------------------------------------------------

const getTotalXP = (languages: LanguageCode[]) => {
  const codes: string[] = languages.length > 0 ? languages : ['wf', 'ba', 'la', 'ff', 'co', 'sw', 'ka', 'yo'];
  return codes.reduce((sum, code) => {
    return sum + parseInt(localStorage.getItem(`${code}-totalXP`) || '0', 10);
  }, 0);
};

const getDailyStreak = (): number => {
  try {
    const raw = localStorage.getItem('jogolinga-daily-streak');
    if (!raw) return 0;
    return JSON.parse(raw).streak ?? 0;
  } catch {
    return 0;
  }
};

const LANG_LABELS: Record<string, string> = {
  wf: 'Wolof',
  ba: 'Bambara',
  la: 'Lingala',
  ff: 'Peul',
  co: 'Copte',
  sw: 'Swahili',
  yo: 'Yoruba',
};

// ---------------------------------------------------------------
// Props
// ---------------------------------------------------------------
interface ProfilePageProps {
  user: {
    id: string;
    email: string;
    name: string;
    picture: string;
    isAuthenticated: boolean;
  } | null;
  userProgress: UserProgress;
  completedCategories: string[];
  availableCategories?: string[];
  currentStreak: number;
  currentLanguage: LanguageCode | null;
  subscriptionTier: SubscriptionTier;
  onLogout: () => void;
  onOpenSubscription: () => void;
  onResetProgress?: () => void;
  onChallengeFriend?: (friendId: string) => void;
}

// ---------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------
const ProfilePage: React.FC<ProfilePageProps> = ({
  user,
  userProgress,
  completedCategories,
  availableCategories,
  currentStreak,
  currentLanguage,
  subscriptionTier,
  onLogout,
  onOpenSubscription,
  onResetProgress,
  onChallengeFriend,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const [showProgression, setShowProgression] = useState(false);
  type ProfileTab = 'collection' | 'settings';
  const [activeTab, setActiveTab] = useState<ProfileTab>('collection');

  // --- Section Pseudo ---
  const [username, setUsername] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // --- Section Communauté ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchNoResult, setSearchNoResult] = useState(false);
  const [following, setFollowing] = useState<UserProfile[]>([]);
  const [leaderboard, setLeaderboard] = useState<UserProfile[]>([]);
  // XP de la semaine (null = fonction serveur indisponible → classement « depuis le début » seul)
  const [weeklyXP, setWeeklyXP] = useState<Map<string, number> | null>(null);
  const [lbMode, setLbMode] = useState<'week' | 'all'>('week');
  const [followingSet, setFollowingSet] = useState<Set<string>>(new Set());
  const [communityLoading, setCommunityLoading] = useState(true);

  const searchTimeout    = useRef<NodeJS.Timeout | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  // --- Profil utilisateur suivi ---
  const [viewedProfile, setViewedProfile] = useState<UserProfile | null>(null);

  // --- Notifications ---
  const [notifPermission, setNotifPermission] = useState<NotifStatus>('default');
  const [remindersOn, setRemindersOn] = useState(true);
  const [notifLoading, setNotifLoading] = useState(false);

  const [notifPrefs, setNotifPrefs] = useState<NotifPrefs>(getLocalNotifPrefs);
  const [prefSaving, setPrefSaving] = useState<NotifPrefKey | null>(null);
  const [prefError, setPrefError] = useState(false);

  useEffect(() => {
    getNotificationStatus().then(setNotifPermission);
    setRemindersOn(areRemindersEnabled());
    loadNotifPrefs().then(setNotifPrefs);
  }, []);

  const handleTogglePref = async (key: NotifPrefKey) => {
    const value = !notifPrefs[key];
    setNotifPrefs(p => ({ ...p, [key]: value }));
    setPrefSaving(key);
    setPrefError(false);
    const ok = await saveNotifPref(key, value);
    setPrefSaving(null);
    if (!ok) {
      setNotifPrefs(p => ({ ...p, [key]: !value }));
      setPrefError(true);
    }
  };

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    const granted = await requestAndSubscribe();
    setNotifLoading(false);
    setNotifPermission(await getNotificationStatus());
    setRemindersOn(areRemindersEnabled());
    if (granted) localStorage.removeItem('jogolinga-notif-banner-snoozed');
  };

  const handleDisableNotifications = async () => {
    setNotifLoading(true);
    await disableReminders();
    setNotifLoading(false);
    setRemindersOn(false);
  };

  // --- Suppression de compte ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleteStatus, setDeleteStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteConfirmWord = t('profile.deleteModal.confirmWord');

  const handleDeleteAccount = async () => {
    if (deleteConfirmInput !== deleteConfirmWord) return;
    setDeleteStatus('loading');
    setDeleteError(null);
    try {
      await secureAuthService.deleteAccount();
      // Vider le localStorage lié à la progression
      localStorage.clear();
      onLogout();
    } catch (err: any) {
      setDeleteError(err.message || t('profile.deleteModal.genericError'));
      setDeleteStatus('error');
    }
  };

  // --- Stats ---
  const totalXP = getTotalXP(currentLanguage ? [currentLanguage] : []);
  const globalXP  = getTotalXP([]);
  const { current: lvl, next: nextLvl, progress: lvlProgress } = getLevel(globalXP, currentLanguage ?? 'wf');
  const dailyStreak = getDailyStreak();
  const wordsLearned = userProgress.learnedWords.size;

  // --- Collectibles ---
  const completedUnitIds = UNIT_CONFIGS
    .filter(u => {
      // Ne garder que les catégories qui existent dans la langue courante.
      // Si availableCategories n'est pas fourni, on prend toutes les catégories de l'unité.
      const validCats = availableCategories?.length
        ? u.categories.filter(c => availableCategories.includes(c))
        : u.categories;
      // L'unité est complète si au moins une catégorie est disponible ET toutes sont complétées.
      return validCats.length > 0 && validCats.every(c => completedCategories.includes(c));
    })
    .map(u => u.id);
  const wordsReviewedCount = Object.values(userProgress.wordProgress).filter(
    (wp: any) => wp?.nextReview != null,
  ).length;
  const collectibles = getCollectibles(currentLanguage);
  const unlockedIds = getUnlockedIds(collectibles, dailyStreak, totalXP, completedUnitIds, wordsReviewedCount);
  const unlockedCount = unlockedIds.size;

  // --- Langues actives (ont au moins 1 mot appris) ---
  const ALL_LANGS: LanguageCode[] = ['wf', 'ba', 'la', 'ff', 'co', 'sw', 'ka', 'yo'];

  // ---------------------------------------------------------------
  // Chargement initial : profil + following + leaderboard
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!user?.id) return;

    const load = async () => {
      setCommunityLoading(true);
      try {
        // Charge ou crée le profil Supabase
        const profile = await getMyProfile(user.id);
        if (profile) {
          setUsername(profile.username ?? '');
          setUsernameInput(profile.username ?? '');
          // Sync display_name depuis Google si absent
          if (!profile.display_name && user.name) {
            await upsertProfile(user.id, { display_name: user.name, avatar_url: user.picture || undefined });
          }
        } else {
          // Crée le profil si inexistant
          await upsertProfile(user.id, {
            display_name: user.name,
            avatar_url: user.picture,
            total_xp: globalXP,
            daily_streak: dailyStreak,
          });
        }

        const [fl, lb, wk] = await Promise.all([
          getFollowing(user.id),
          getLeaderboard(user.id),
          getWeeklyXP(),
        ]);
        setFollowing(fl);
        setLeaderboard(lb);
        setWeeklyXP(wk);
        setFollowingSet(new Set(fl.map((f) => f.id)));
      } catch (err) {
} finally {
        setCommunityLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ---------------------------------------------------------------
  // Sauvegarde du pseudo
  // ---------------------------------------------------------------
  const handleSaveUsername = useCallback(async () => {
    if (!user?.id || !usernameInput.trim()) return;
    const clean = usernameInput.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!clean) return;

    setUsernameStatus('saving');
    try {
      await upsertProfile(user.id, { username: clean, display_name: user.name });
      setUsername(clean);
      setUsernameStatus('saved');
      setTimeout(() => setUsernameStatus('idle'), 2000);
    } catch {
      setUsernameStatus('error');
      setTimeout(() => setUsernameStatus('idle'), 3000);
    }
  }, [user, usernameInput]);

  // ---------------------------------------------------------------
  // Recherche d'amis (debounce 400ms)
  // ---------------------------------------------------------------
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearchNoResult(false);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      setSearchNoResult(false);
      try {
        const results = await searchUsers(searchQuery);
        const filtered = results.filter((r) => r.id !== user?.id);
        setSearchResults(filtered);
        setSearchNoResult(filtered.length === 0);
      } catch {
        setSearchResults([]);
        setSearchNoResult(false);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchQuery, user?.id]);

  // ---------------------------------------------------------------
  // Fermer les résultats au clic extérieur
  // ---------------------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setSearchResults([]);
        setSearchNoResult(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ---------------------------------------------------------------
  // Follow / Unfollow
  // ---------------------------------------------------------------
  const handleFollow = useCallback(async (targetId: string) => {
    if (!user?.id) return;
    try {
      if (followingSet.has(targetId)) {
        await unfollowUser(user.id, targetId);
        setFollowingSet((prev) => { const s = new Set(prev); s.delete(targetId); return s; });
        setFollowing((prev) => prev.filter((f) => f.id !== targetId));
      } else {
        await followUser(user.id, targetId);
        setFollowingSet((prev) => new Set([...prev, targetId]));
        // Recharge following + leaderboard
        const [fl, lb, wk] = await Promise.all([getFollowing(user.id), getLeaderboard(user.id), getWeeklyXP()]);
        setFollowing(fl);
        setLeaderboard(lb);
        setWeeklyXP(wk);
      }
    } catch (err) {
}
  }, [user?.id, followingSet]);

  // ---------------------------------------------------------------
  // Avatar (initiales si pas de photo)
  // ---------------------------------------------------------------
  const renderAvatar = () => {
    if (user?.picture) {
      return <img src={user.picture} alt={t('profile.identity.avatarAlt') as string} className="profile-avatar-img" />;
    }
    const initials = (user?.name ?? 'U')
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    return <div className="profile-avatar-initials">{initials}</div>;
  };

  // ---------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------
  if (showProgression && currentLanguage) {
    return (
      <ProgressionPage
        languageCode={currentLanguage}
        userProgress={userProgress}
        currentStreak={currentStreak}
        onBack={() => setShowProgression(false)}
      />
    );
  }

  return (
    <>
    <div className={`profile-page ${theme}`}>
      <TribalBorder />
      <div className="profile-container">

        {/* =========================================================
            EN-TÊTE : avatar + nom + niveau/série
            ========================================================= */}
        <div className="profile-header">
          <div className="profile-avatar">{renderAvatar()}</div>
          <div className="profile-header-info">
            <h2 className="profile-display-name">{user?.name ?? t('profile.identity.defaultName')}</h2>
            <p className="profile-header-meta">
              {t('profile.header.levelStreak', { level: lvl.level, days: dailyStreak })}
            </p>
          </div>
          {user && (
            <button className="profile-header-logout" onClick={onLogout} title={t('profile.identity.logout') as string}>
              <LogOut size={16} strokeWidth={1.75} />
            </button>
          )}
        </div>

        {/* Bannière abonnement — cliquable dans tous les cas : ouvre le
            statut détaillé (premium) ou l'offre d'abonnement (free) */}
        <div
          className={`profile-subscription-banner ${subscriptionTier === SubscriptionTier.PREMIUM ? 'premium' : 'free'}`}
          onClick={onOpenSubscription}
          style={{ cursor: 'pointer' }}
        >
          {subscriptionTier === SubscriptionTier.PREMIUM ? (
            <>
              <span className="profile-sub-icon"><Award size={20} strokeWidth={1.75} /></span>
              <div className="profile-sub-text">
                <span className="profile-sub-title">{t('profile.subscription.premiumTitle')}</span>
                <span className="profile-sub-desc">{t('profile.subscription.premiumDesc')}</span>
              </div>
              <span className="profile-sub-active">{t('profile.subscription.active')}</span>
              <span className="profile-sub-arrow">›</span>
            </>
          ) : (
            <>
              <span className="profile-sub-icon"><Unlock size={20} strokeWidth={1.75} /></span>
              <div className="profile-sub-text">
                <span className="profile-sub-title">{t('profile.subscription.freeTitle')}</span>
                <span className="profile-sub-desc">{t('profile.subscription.freeDesc')}</span>
              </div>
              <span className="profile-sub-arrow">›</span>
            </>
          )}
        </div>

        {/* =========================================================
            ONGLETS : Progression / Collection / Réglages
            ========================================================= */}
        <div className="profile-tabs" role="tablist">
          {currentLanguage && (
            <button
              className="profile-tab"
              role="tab"
              aria-selected={false}
              onClick={() => setShowProgression(true)}
            >
              {t('profile.tabs.progression')}
            </button>
          )}
          <button
            className={`profile-tab${activeTab === 'collection' ? ' profile-tab--active' : ''}`}
            role="tab"
            aria-selected={activeTab === 'collection'}
            onClick={() => setActiveTab('collection')}
          >
            {t('profile.tabs.collection')}
          </button>
          <button
            className={`profile-tab${activeTab === 'settings' ? ' profile-tab--active' : ''}`}
            role="tab"
            aria-selected={activeTab === 'settings'}
            onClick={() => setActiveTab('settings')}
          >
            {t('profile.tabs.settings')}
          </button>
        </div>

        {/* =========================================================
            ONGLET COLLECTION
            ========================================================= */}
        {activeTab === 'collection' && (
        <section className="profile-section profile-collection-section">
          <div className="profile-collection-header">
            <h3 className="profile-section-title" style={{ margin: 0 }}>{t('profile.sections.collection.title')}</h3>
            {collectibles.length > 0 && (
              <span className="profile-collection-count">{unlockedCount} / {collectibles.length}</span>
            )}
          </div>
          {collectibles.length > 0 && (
            <div className="profile-collection-progress">
              <div style={{ width: `${(unlockedCount / collectibles.length) * 100}%` }} />
            </div>
          )}
          {collectibles.length > 0 ? (
            <div className="profile-collection-grid">
              {collectibles.map(c => {
                const unlocked = unlockedIds.has(c.id);
                return (
                  <div
                    key={c.id}
                    className={`profile-collectible ${unlocked ? 'profile-collectible--unlocked' : 'profile-collectible--locked'}`}
                    title={unlocked ? `${c.name} — ${c.desc}` : c.hint}
                  >
                    {unlocked
                      ? <CollectibleIcon path={c.icon} className="profile-collectible__icon" />
                      : <DiamondIcon className="profile-collectible__icon" />}
                    <span className="profile-collectible__name">{c.name}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="profile-collection-soon">{t('profile.sections.collection.soon')}</p>
          )}
        </section>
        )}

        {/* =========================================================
            ONGLET RÉGLAGES
            ========================================================= */}
        {activeTab === 'settings' && (
        <>
        <section className="profile-section">
          <h3 className="profile-section-title">{t('profile.sections.pseudo.title')}</h3>
          <p className="profile-section-desc">
            {t('profile.sections.pseudo.desc')}
          </p>
          <div className="profile-username-row">
            <span className="profile-username-at">@</span>
            <input
              className="profile-username-input"
              type="text"
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              placeholder={t('profile.sections.pseudo.placeholder') as string}
              maxLength={30}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveUsername()}
            />
            <button
              className="profile-btn-gold"
              onClick={handleSaveUsername}
              disabled={usernameStatus === 'saving'}
            >
              {usernameStatus === 'saving' ? t('common.loading') :
               usernameStatus === 'saved'  ? t('profile.sections.pseudo.saved') :
               usernameStatus === 'error'  ? t('profile.sections.pseudo.error') : t('profile.sections.pseudo.save')}
            </button>
          </div>
        </section>


        {/* =========================================================
            RAPPELS (NOTIFICATIONS)
            ========================================================= */}
        <section className="profile-section">
          <h3 className="profile-section-title"><Bell size={18} strokeWidth={1.75} style={{ marginRight: '6px', verticalAlign: '-3px' }} />{t('profile.sections.reminders.title')}</h3>
          <p className="profile-section-desc">
            {t('profile.sections.reminders.desc')}
          </p>

          {notifPermission === 'unsupported' && (
            <p className="profile-notif-status profile-notif-status--warn">
              {t('profile.sections.reminders.unsupported')}
            </p>
          )}

          {notifPermission === 'granted' && (
            <div className={`profile-notif-status ${remindersOn ? 'profile-notif-status--ok' : 'profile-notif-status--warn'}`}>
              <span className="profile-notif-text">
                {remindersOn ? (
                  <><span className="profile-notif-check"><Check size={14} strokeWidth={2.5} /></span> {t('profile.sections.reminders.enabled')}</>
                ) : (
                  t('profile.sections.reminders.disabled')
                )}
              </span>
              <label className="profile-notif-toggle" aria-label={remindersOn ? t('profile.sections.reminders.ariaDisable') as string : t('profile.sections.reminders.ariaEnable') as string}>
                <input
                  type="checkbox"
                  checked={remindersOn}
                  disabled={notifLoading}
                  onChange={() => (remindersOn ? handleDisableNotifications() : handleEnableNotifications())}
                />
                <span className="profile-notif-toggle-slider" />
              </label>
            </div>
          )}

          {notifPermission === 'granted' && remindersOn && (
            <ul className="profile-notif-types">
              {([
                { key: 'reminders',  Icon: Flame },
                { key: 'culture',    Icon: Globe },
                { key: 'challenges', Icon: Swords },
              ] as const).map(({ key, Icon }) => (
                <li key={key} className="profile-notif-type">
                  <Icon size={18} strokeWidth={1.75} className="profile-notif-type-icon" aria-hidden="true" />
                  <span className="profile-notif-type-text">
                    <span className="profile-notif-type-label">{t(`profile.sections.reminders.types.${key}.label`)}</span>
                    <span className="profile-notif-type-desc">{t(`profile.sections.reminders.types.${key}.desc`)}</span>
                  </span>
                  <label className="profile-notif-toggle">
                    <input
                      type="checkbox"
                      checked={notifPrefs[key]}
                      disabled={prefSaving === key}
                      onChange={() => handleTogglePref(key)}
                      aria-label={t(`profile.sections.reminders.types.${key}.label`) as string}
                    />
                    <span className="profile-notif-toggle-slider" />
                  </label>
                </li>
              ))}
            </ul>
          )}

          {prefError && (
            <p className="profile-notif-status profile-notif-status--denied" role="alert">
              {t('profile.sections.reminders.saveError')}
            </p>
          )}

          {notifPermission === 'denied' && (
            <p className="profile-notif-status profile-notif-status--denied">
              {t('profile.sections.reminders.denied')}
            </p>
          )}

          {notifPermission === 'default' && (
            <button
              className="profile-btn-gold profile-notif-btn"
              onClick={handleEnableNotifications}
              disabled={notifLoading}
            >
              {notifLoading ? t('common.loading') : t('profile.sections.reminders.enableButton')}
            </button>
          )}
        </section>

        {/* =========================================================
            LANGUE DE L'INTERFACE
            ========================================================= */}
        <section className="profile-section">
          <h3 className="profile-section-title"><Globe size={18} strokeWidth={1.75} style={{ marginRight: '6px', verticalAlign: '-3px' }} />{t('profile.sections.language.title')}</h3>
          <p className="profile-section-desc">{t('profile.sections.language.desc')}</p>
          <InterfaceLanguageSelector />
        </section>

        {/* ── Zone danger ─────────────────────────────── */}
        <section className="profile-section profile-danger-zone">
          <h3 className="profile-section-title" style={{ color: 'var(--color-error)' }}><AlertTriangle size={17} strokeWidth={1.75} style={{ marginRight: '6px', verticalAlign: '-3px' }} />{t('profile.sections.dangerZone.title')}</h3>
          {onResetProgress && (
            <>
              <p className="profile-danger-desc">
                {t('profile.sections.dangerZone.resetDesc')}
              </p>
              <button
                className="profile-danger-btn"
                style={{ background: 'var(--gold-primary)', marginBottom: '12px' }}
                onClick={() => {
                  const ok = window.confirm(t('profile.sections.dangerZone.resetConfirm') as string);
                  if (ok) onResetProgress();
                }}
              >
                <EmojiText text={t('profile.sections.dangerZone.resetButton') as string} />
              </button>
            </>
          )}
          <p className="profile-danger-desc">
            {t('profile.sections.dangerZone.deleteDesc')}
          </p>
          <button
            className="profile-danger-btn"
            onClick={() => { setShowDeleteModal(true); setDeleteConfirmInput(''); setDeleteStatus('idle'); setDeleteError(null); }}
          >
            {t('profile.sections.dangerZone.deleteButton')}
          </button>
        </section>
        </>
        )}

        {/* =========================================================
            COMMUNAUTÉ (toujours visible, hors onglets)
            ========================================================= */}
        <section className="profile-section">
          <h3 className="profile-section-title"><Users size={18} strokeWidth={1.75} style={{ marginRight: '6px', verticalAlign: '-3px' }} />{t('profile.sections.community.title')}</h3>

          {/* Recherche */}
          <div ref={searchContainerRef}>
            <div className="profile-search-row">
              <input
                className="profile-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('profile.sections.community.searchPlaceholder') as string}
              />
              {searchLoading && <span className="profile-search-spinner"><Picto name="sablier" /></span>}
            </div>

            {/* Résultats de recherche */}
            {searchResults.length > 0 && (
              <div className="profile-search-results">
                {searchResults.map((u) => (
                  <div key={u.id} className="profile-user-card">
                    <div className="profile-user-avatar">
                      {u.avatar_url
                        ? <img src={u.avatar_url} alt="" />
                        : <span>{(u.display_name ?? u.username ?? '?')[0].toUpperCase()}</span>}
                    </div>
                    <div className="profile-user-info">
                      <span className="profile-user-name">{u.display_name ?? u.username}</span>
                      {u.username && <span className="profile-user-handle">@{u.username}</span>}
                    </div>
                    {followingSet.has(u.id)
                      ? <span className="profile-follow-badge">{t('profile.sections.community.followed')}</span>
                      : <button className="profile-follow-btn" onClick={() => handleFollow(u.id)}>{t('profile.sections.community.follow')}</button>
                    }
                  </div>
                ))}
              </div>
            )}

            {/* Aucun résultat */}
            {searchNoResult && !searchLoading && (
              <p className="profile-search-no-result">
                {t('profile.sections.community.noResult', { query: searchQuery })}
              </p>
            )}
          </div>

          {/* Classement unifié : moi + personnes suivies */}
          {!communityLoading && leaderboard.length > 0 && (
            <>
              <h4 className="profile-subsection-title"><EmojiText text={t('profile.sections.community.leaderboard', { count: leaderboard.length }) as string} /></h4>
              {weeklyXP && (
                <div className="profile-tabs profile-lb-tabs" role="tablist">
                  {(['week', 'all'] as const).map(m => (
                    <button
                      key={m}
                      className={`profile-tab${lbMode === m ? ' profile-tab--active' : ''}`}
                      role="tab"
                      aria-selected={lbMode === m}
                      onClick={() => setLbMode(m)}
                    >
                      {t(m === 'week' ? 'profile.sections.community.tabWeek' : 'profile.sections.community.tabAll')}
                    </button>
                  ))}
                </div>
              )}
              {weeklyXP && lbMode === 'week' && (() => {
                const { days, hours } = timeUntilWeekReset();
                const time = days > 0
                  ? t('profile.sections.community.timeDaysHours', { d: days, h: hours })
                  : t('profile.sections.community.timeHours', { h: hours });
                return <p className="profile-lb-reset">{t('profile.sections.community.weekEnds', { time })}</p>;
              })()}
              <div className="profile-leaderboard">
                {(weeklyXP && lbMode === 'week'
                  ? [...leaderboard].sort((a, b) =>
                      (weeklyXP.get(b.id) ?? 0) - (weeklyXP.get(a.id) ?? 0) || displayXP(b) - displayXP(a))
                  : leaderboard
                ).map((u, idx) => {
                  const isMe = u.id === user?.id;
                  const rank = `${idx + 1}`;
                  const displayName = u.display_name ?? u.username ?? (isMe ? user?.name : null) ?? t('profile.sections.community.anonymous');
                  const initial = displayName[0].toUpperCase();
                  const langInfo = getLanguageInfo(u.learning_language);
                  return (
                    <div
                      key={u.id}
                      className={`profile-lb-row ${isMe ? 'profile-lb-me' : 'profile-user-card-clickable'}`}
                      onClick={() => { if (!isMe) setViewedProfile(u); }}
                    >
                      <span className={`profile-lb-rank${idx < 3 ? ' profile-lb-rank--top' : ''}`}>{rank}</span>
                      <div className="profile-user-avatar small">
                        {u.avatar_url
                          ? <img src={u.avatar_url} alt="" />
                          : <span>{initial}</span>}
                      </div>
                      <div className="profile-lb-info">
                        <span className="profile-lb-name">
                          {displayName}{isMe ? t('profile.sections.community.me') : ''}
                        </span>
                        {langInfo && (
                          <span className="profile-user-learning">
                            <EmojiText text={langInfo.flag} /> {langInfo.name}
                            {u.learning_language_xp != null && u.learning_language_xp > 0
                              ? ` · ${u.learning_language_xp.toLocaleString()} XP`
                              : ''}
                          </span>
                        )}
                      </div>
                      <span className="profile-lb-xp">
                        {(weeklyXP && lbMode === 'week' ? (weeklyXP.get(u.id) ?? 0) : displayXP(u)).toLocaleString()} XP
                      </span>
                      {!isMe && onChallengeFriend && (
                        <button
                          className="profile-lb-challenge-btn"
                          onClick={e => { e.stopPropagation(); onChallengeFriend(u.id); }}
                          title={t('profile.sections.community.challengeTitle', { name: displayName }) as string}
                        >
                          <Swords size={15} strokeWidth={1.75} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {communityLoading && (
            <p className="profile-loading">{t('profile.sections.community.loading')}</p>
          )}

          {!communityLoading && leaderboard.length === 0 && following.length === 0 && (
            <p className="profile-empty">
              {t('profile.sections.community.empty')}
            </p>
          )}
        </section>

      </div>
      <Footer />
    </div>

    {/* ── Modal confirmation suppression ──────────────── */}
    {showDeleteModal && (
      <div className="profile-delete-overlay" onClick={() => setShowDeleteModal(false)}>
        <div className="profile-delete-modal" onClick={e => e.stopPropagation()}>
          <div className="profile-delete-modal-icon"><AlertTriangle size={32} strokeWidth={1.5} /></div>
          <h3 className="profile-delete-modal-title">{t('profile.deleteModal.title')}</h3>
          <p className="profile-delete-modal-text">
            {t('profile.deleteModal.textBefore')} <strong>{t('profile.deleteModal.textStrong')}</strong>{t('profile.deleteModal.textAfter')}
          </p>
          <p className="profile-delete-modal-label">
            {t('profile.deleteModal.labelBefore')} <strong>{deleteConfirmWord}</strong> {t('profile.deleteModal.labelAfter')}
          </p>
          <input
            className="profile-delete-modal-input"
            type="text"
            value={deleteConfirmInput}
            onChange={e => setDeleteConfirmInput(e.target.value)}
            placeholder={deleteConfirmWord}
            autoFocus
          />
          {deleteError && (
            <p className="profile-delete-modal-error">{deleteError}</p>
          )}
          <div className="profile-delete-modal-actions">
            <button
              className="profile-delete-modal-cancel"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteStatus === 'loading'}
            >
              {t('profile.deleteModal.cancel')}
            </button>
            <button
              className="profile-delete-modal-confirm"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmInput !== deleteConfirmWord || deleteStatus === 'loading'}
            >
              {deleteStatus === 'loading' ? t('profile.deleteModal.confirmLoading') : t('profile.deleteModal.confirm')}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* ── Modal profil utilisateur suivi ──────────────── */}
    {viewedProfile && (() => {
      const vp = viewedProfile;
      const langInfo = getLanguageInfo(vp.learning_language);
      const displayName = vp.display_name ?? vp.username ?? t('profile.sections.community.anonymous');
      const initial = displayName[0].toUpperCase();
      const lastActiveDays = vp.last_active
        ? Math.floor((Date.now() - new Date(vp.last_active).getTime()) / 86400000)
        : null;
      return (
        <div className="profile-view-overlay" onClick={() => setViewedProfile(null)}>
          <div className="profile-view-modal" onClick={e => e.stopPropagation()}>
            <button className="profile-view-close" onClick={() => setViewedProfile(null)}><X size={16} strokeWidth={2} /></button>
            <div className="profile-view-avatar">
              {vp.avatar_url
                ? <img src={vp.avatar_url} alt="" />
                : <span>{initial}</span>}
            </div>
            <div className="profile-view-name">{displayName}</div>
            {vp.username && <div className="profile-view-handle">@{vp.username}</div>}

            <div className="profile-view-stats">
              <div className="profile-view-stat">
                <span className="profile-view-stat-value">{displayXP(vp).toLocaleString()}</span>
                <span className="profile-view-stat-label">{t('profile.viewModal.totalXp')}</span>
              </div>
              <div className="profile-view-stat">
                <span className="profile-view-stat-value"><Flame size={15} strokeWidth={1.75} style={{ verticalAlign: '-2px' }} /> {vp.daily_streak ?? 0}</span>
                <span className="profile-view-stat-label">{t('profile.viewModal.streakDays')}</span>
              </div>
              {langInfo && (
                <div className="profile-view-stat">
                  <span className="profile-view-stat-value"><EmojiText text={langInfo.flag} /> {langInfo.name}</span>
                  <span className="profile-view-stat-label">
                    {t('profile.viewModal.learningLanguage')}
                    {vp.learning_language_xp != null && vp.learning_language_xp > 0
                      ? ` · ${vp.learning_language_xp.toLocaleString()} XP`
                      : ''}
                  </span>
                </div>
              )}
              {lastActiveDays !== null && (
                <div className="profile-view-stat">
                  <span className="profile-view-stat-value">
                    {lastActiveDays === 0 ? t('profile.viewModal.today') : lastActiveDays === 1 ? t('profile.viewModal.yesterday') : t('profile.viewModal.daysAgo', { count: lastActiveDays })}
                  </span>
                  <span className="profile-view-stat-label">{t('profile.viewModal.lastActive')}</span>
                </div>
              )}
            </div>

            {onChallengeFriend && (
              <button
                className="profile-view-challenge-btn"
                onClick={() => { setViewedProfile(null); onChallengeFriend(vp.id); }}
              >
                <EmojiText text={t('profile.viewModal.challenge', { name: displayName }) as string} />
              </button>
            )}
          </div>
        </div>
      );
    })()}
    </>
  );
};

export default ProfilePage;
